/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Dec 13 2024
 **/

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { Maybe } from "@/frameworks/libraries/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { Configure } from "@/frameworks/libraries/utilities";
import { _P, _T } from "@rfice/language";
import { useUsers } from "@/frameworks/core/API";
import { GROUP_POPOVER_ID, POPOVER_ID } from "@/frameworks/core/commons";
import { Properties } from "@/frameworks/libraries";
import { Avatar, AvatarStatusColorMap, AvatarStatusTextMap } from "@/frameworks/core/components";
import { useGuestSignupConfirm, usePeopleListActions, usePhoneFavorite } from "@/frameworks/core/hooks";
import { usePersistActions } from "@/frameworks/core/persistence";
import { i18nSpaceLocationName } from "@/frameworks/core/utils";
import { AvatarStatus, Button, Column, Icon, Image, Row, Typography } from "@/frameworks/designStuff/components";

import * as S from "./emotion";
import MiniProfileContentTemplate, { MiniProfileContentTemplateProps } from "./MiniProfileContentTemplate";
import useMiniProfileContentAction from "./useMiniProfileContentAction";

type CompoundMiniProfileProps = {
	userData: Partial<WebServices.User.UserInfo> & { statusName?: string; statusEmoticonPath?: string };
	popoverID: POPOVER_ID | GROUP_POPOVER_ID;
};

/**
 * !important: add service logic in here
 */

export default function CompundMiniProfile({ userData, popoverID }: CompoundMiniProfileProps) {
	const favorite = usePhoneFavorite();
	const miniProfileContentHandlers = useMiniProfileContentAction();
	const guestSignupConfirm = useGuestSignupConfirm();
	const { handleFriendToggle } = usePeopleListActions().handlers;
	const properties = useProperties();
	const isGuest = Properties.property.isGuest;

	//내가 스페이스에 있다면
	const userActions = usePersistActions().users;

	const userAPI = useUsers();
	const { data: userInfos } = useQuery({
		queryKey: ["userInfo", userData?.userId],
		queryFn: async () => (await userAPI.inquiry.execute({ userId: userData?.userId })).data,
		refetchOnMount: true
	});

	/**
	 * @description currentSpace는 stomp로 인한 실시간 데이터 조회가 안됨으로 UI의 fetch API 의존해야함
	 */
	useEffect(() => {
		userData.id && userActions.refresh_user(userData.id);
	}, []);

	const buttonBackgroundColor = useMemo(() => (isGuest ? "rgba(50, 51, 56, 0.08)" : ""), [isGuest]);

	const miniProfileContentProps = {
		avatar: {
			render: () => {
				return <Avatar id={userData.id || ""} kind="people_64" showMiniProfile={false} />;
			}
		},
		content: {
			render: () => {
				return (
					<>
						<Column css={S.fullWidthStyle}>
							<Row horizonAlign="distribute" css={S.fullWidthStyle}>
								<Typography.p
									typoSize="Title1_B"
									ellipsis
									style={{
										whiteSpace: "nowrap",
										overflow: "hidden",
										maxWidth: "140px"
									}}
								>
									{userData.name}
									{userData.me ? " (" + _T("나") + ")" : ""}
								</Typography.p>
							</Row>
							<Typography.p
								typoSize="Body5"
								ellipsis
								style={{
									whiteSpace: "nowrap",
									overflow: "hidden",
									maxWidth: "140px",
									paddingLeft: "2px"
								}}
							>
								{userData.username}
							</Typography.p>
						</Column>
						<Column css={S.fullWidthStyle} gap={8}>
							<Maybe
								test={Boolean(userData?.userStatus || userData?.statusName)}
								fallback={
									<Row verticalAlign="center" gap={"4px"} css={S.fullWidthStyle}>
										<AvatarStatus
											size="Medium"
											status={AvatarStatusColorMap[userData.activeStatus ?? "OFFLINE"] ?? "Gray"}
											style={{ boxSizing: "initial" }}
										/>{" "}
										{AvatarStatusTextMap(userData.activeStatus ?? "OFFLINE")}
									</Row>
								}
							>
								<Maybe test={userData.authority !== "GROUP_USER"}>
									<Row verticalAlign="center" gap={2} css={S.fullWidthStyle}>
										<Maybe
											test={Boolean(
												userInfos?.userStatusEmoticonPath || userData?.statusEmoticonPath
											)}
										>
											<Image
												style={{ width: "20px", height: "20px" }}
												src={
													properties.default.webDomain +
													(userInfos?.userStatusEmoticonPath || userData?.statusEmoticonPath)
												}
											/>
										</Maybe>
										<Typography.p
											css={[S.LongText]}
											typoSize="Body5_SB"
											style={{ paddingLeft: "2px" }}
										>
											{userInfos?.userStatus || userData?.statusName || "유저 상태"}
										</Typography.p>
									</Row>
								</Maybe>
							</Maybe>

							<Row css={[S.fullWidthStyle]} style={{ padding: 0 }}>
								<Icon css={[S.iconLocation]} name="Place16" size={20} />
								{/*{userData.currentSpace && (*/}
								{/*	<Icon*/}
								{/*		css={[iconLocation]}*/}
								{/*		name={*/}
								{/*			userData.currentSpace.spaceType === "MEETING"*/}
								{/*				? "CalendarMonth24"*/}
								{/*				: "Place16"*/}
								{/*		}*/}
								{/*		size={20}*/}
								{/*	/>*/}
								{/*)}*/}
								<Typography.p css={[S.LongText]} maxLine={4} typoSize="Body5_SB">
									{i18nSpaceLocationName({
										currentSpace: userData?.currentSpace,
										currentConference: userData?.currentConference,
										activeStatus: userData?.activeStatus
									})}
								</Typography.p>
								{userData?.activeDeviceTypes?.[0] !== "WEB" && (
									<Icon name="PhoneOffUnique24" style={{ marginLeft: 4 }} size={20} />
								)}
								{/*{userData.activeDeviceTypes[0] === "Phone" && <Icon name="PhoneOnUnique24" />}*/}
							</Row>
							<Maybe test={userData.authority !== "GROUP_USER"}>
								<Maybe test={userData.me || userData.blockedByMe || userData.blockedMe}>
									<Maybe.Fallback>
										<Row gap={5}>
											<Maybe test={userData?.activeStatus !== "OFFLINE"}>
												<Button
													style={{ backgroundColor: buttonBackgroundColor }}
													filled="secondary"
													size="mini"
													onClick={() =>
														isGuest
															? guestSignupConfirm(() => {})
															: miniProfileContentHandlers.handleFollowUser(
																	userData.id || ""
																)
													}
												>
													{_T("따라가기")}
												</Button>
												<Maybe test={userData.allowFollowMe ?? false}>
													<Button
														filled="secondary"
														size="mini"
														onClick={() =>
															isGuest
																? guestSignupConfirm(() => {})
																: miniProfileContentHandlers.handleUserCallToMe(
																		userData.id || ""
																	)
														}
													>
														{_T("불러오기")}
													</Button>
												</Maybe>
											</Maybe>
										</Row>
									</Maybe.Fallback>
								</Maybe>
							</Maybe>
						</Column>
					</>
				);
			}
		},
		headerIconList: {
			render: () => {
				return (
					<>
						<Maybe
							test={!(userData.blockedByMe || userData.blockedMe) && Configure.userID !== userData.userId}
						>
							<Button.Icon
								style={{ backgroundColor: buttonBackgroundColor }}
								name={userData.friend ? "FollowingUnique24" : "AddMember24"}
								variant="text"
								size="medium"
								onClick={(e) => {
									if (isGuest) {
										guestSignupConfirm(() => {});
									} else {
										e.stopPropagation();
										handleFriendToggle(userData);
									}
								}}
							/>
						</Maybe>
						<Button.Icon
							name="Close24"
							variant="text"
							size="medium"
							onClick={() => miniProfileContentHandlers.handleHide(popoverID, userData.id || "")}
						/>
					</>
				);
			}
		},
		footer: {
			render: () => {
				return (
					<S.ProfileButtonArea gap={12}>
						<Maybe test={userData.blockedByMe}>
							<Column gap={7}>
								<Typography.p typoSize="Body5">
									{_P("차단된 사용자 입니다. <br />차단을 해제해야 소통이 가능합니다.")}
								</Typography.p>
								<Button
									filled="secondary"
									size="mini"
									onClick={() => miniProfileContentHandlers.handleBlocked(userData)}
								>
									{_T("차단해제")}
								</Button>
							</Column>
							<Maybe.Fallback>
								<Maybe test={userData.blockedMe}>
									<Maybe.Fallback>
										<Button.Icon
											style={{ backgroundColor: buttonBackgroundColor }}
											name="Chat0124"
											size="medium"
											variant="text"
											onClick={() =>
												isGuest
													? guestSignupConfirm(() => {})
													: miniProfileContentHandlers.handleChat(userData.id || "")
											}
										/>
										<Maybe test={userData.me}>
											<Maybe.Fallback>
												<Button.Icon
													style={{ backgroundColor: buttonBackgroundColor }}
													name="Call0124"
													size="medium"
													variant="text"
													onClick={(e) =>
														isGuest
															? guestSignupConfirm(() => {})
															: miniProfileContentHandlers.handleCall(userData.id || "", {
																	anchorEl: e.currentTarget as HTMLElement,
																	popoverID: GROUP_POPOVER_ID.AVATAR_MINI_PROFILE,
																	direction: {
																		x: "center",
																		y: "top"
																	}
																})
													}
												/>
											</Maybe.Fallback>
										</Maybe>
										<Maybe
											test={
												!(
													userData?.myRoomAccessAllowType === "PRIVATE" ||
													userData?.myRoomAccessAllowType === "ALL_PRIVATE" ||
													(userData?.myRoomAccessAllowType === "PUBLIC" && !userData?.friend)
												) || Boolean(userData?.me)
											}
										>
											<Button.Icon
												style={{ backgroundColor: buttonBackgroundColor }}
												name="Home24"
												size="medium"
												variant="text"
												onClick={() =>
													isGuest
														? guestSignupConfirm(() => {})
														: miniProfileContentHandlers.handleFollowHome({
																userID: userData.id || ""
															})
												}
											/>
										</Maybe>
										<Maybe test={userData.me}>
											<Maybe.Fallback>
												<Button.Icon
													style={{ backgroundColor: buttonBackgroundColor }}
													name={userData.callFavorite ? "StarFill24" : "Star24"}
													size="medium"
													variant="text"
													onClick={() =>
														isGuest
															? guestSignupConfirm(() => {})
															: miniProfileContentHandlers.handleFavorites({
																	active: favorite.has(userData.userId || ""),
																	userID: userData.userId || ""
																})
													}
												/>
											</Maybe.Fallback>
										</Maybe>
									</Maybe.Fallback>
								</Maybe>
							</Maybe.Fallback>
						</Maybe>
					</S.ProfileButtonArea>
				);
			}
		}
	} as MiniProfileContentTemplateProps;
	return <MiniProfileContentTemplate {...miniProfileContentProps} />;
}
